import type { DesignElement, ElementCategory } from '@canvas/shared-types';
import { diagramPreviewUrl } from '@/data/elementPreviewSvgs';

/** Extra Element panel categories for CorelDRAW-style diagramming. */
export const DIAGRAM_CATEGORIES: ElementCategory[] = [
  { id: 'flowchart', label: 'Flowchart', kinds: ['diagram'], order: 5 },
  { id: 'uml', label: 'UML', kinds: ['diagram'], order: 6 },
  { id: 'arrows', label: 'Arrows', kinds: ['diagram'], order: 7 },
];

const ink = '#152033';
const teal = '#0d7377';
const slate = '#e8eef4';
const gold = '#e5c07b';
const sky = '#1b4d89';

function item(
  partial: Omit<DesignElement, 'kind' | 'audience' | 'license' | 'preview'> & {
    factory: Extract<DesignElement['factory'], { type: 'diagram' }>;
  }
): DesignElement {
  return {
    ...partial,
    kind: 'diagram',
    audience: 'all',
    license: 'free',
    preview: {
      mode: 'image',
      value: diagramPreviewUrl(partial.factory.diagram),
      label: partial.name,
    },
  };
}

export const DIAGRAM_CATALOG: DesignElement[] = [
  // —— Flowchart ——
  item({
    elementId: 'flow-process',
    name: 'Process',
    category: 'flowchart',
    tags: ['flowchart', 'process', 'corel'],
    defaultWidth: 200,
    defaultHeight: 100,
    factory: {
      type: 'diagram',
      diagram: 'process',
      fill: teal,
      stroke: ink,
      strokeWidth: 2,
      label: 'Process',
    },
  }),
  item({
    elementId: 'flow-decision',
    name: 'Decision',
    category: 'flowchart',
    tags: ['flowchart', 'diamond', 'decision'],
    defaultWidth: 180,
    defaultHeight: 140,
    factory: {
      type: 'diagram',
      diagram: 'decision',
      fill: gold,
      stroke: ink,
      strokeWidth: 2,
      label: 'Decision?',
    },
  }),
  item({
    elementId: 'flow-terminator',
    name: 'Terminator',
    category: 'flowchart',
    tags: ['flowchart', 'start', 'end'],
    defaultWidth: 200,
    defaultHeight: 72,
    factory: {
      type: 'diagram',
      diagram: 'terminator',
      fill: sky,
      stroke: ink,
      strokeWidth: 2,
      label: 'Start / End',
    },
  }),
  item({
    elementId: 'flow-data',
    name: 'Data / I/O',
    category: 'flowchart',
    tags: ['flowchart', 'parallelogram', 'io'],
    defaultWidth: 200,
    defaultHeight: 100,
    factory: {
      type: 'diagram',
      diagram: 'data',
      fill: '#5b6abf',
      stroke: ink,
      strokeWidth: 2,
      label: 'Data',
    },
  }),
  item({
    elementId: 'flow-document',
    name: 'Document',
    category: 'flowchart',
    tags: ['flowchart', 'document'],
    defaultWidth: 180,
    defaultHeight: 140,
    factory: {
      type: 'diagram',
      diagram: 'document',
      fill: slate,
      stroke: ink,
      strokeWidth: 2,
      label: 'Document',
    },
  }),
  item({
    elementId: 'flow-manual-input',
    name: 'Manual input',
    category: 'flowchart',
    tags: ['flowchart', 'input'],
    defaultWidth: 200,
    defaultHeight: 100,
    factory: {
      type: 'diagram',
      diagram: 'manual-input',
      fill: '#b54708',
      stroke: ink,
      strokeWidth: 2,
      label: 'Input',
    },
  }),
  item({
    elementId: 'flow-delay',
    name: 'Delay',
    category: 'flowchart',
    tags: ['flowchart', 'delay'],
    defaultWidth: 180,
    defaultHeight: 100,
    factory: {
      type: 'diagram',
      diagram: 'delay',
      fill: '#6b8f71',
      stroke: ink,
      strokeWidth: 2,
      label: 'Delay',
    },
  }),
  item({
    elementId: 'flow-display',
    name: 'Display',
    category: 'flowchart',
    tags: ['flowchart', 'display'],
    defaultWidth: 200,
    defaultHeight: 110,
    factory: {
      type: 'diagram',
      diagram: 'display',
      fill: '#0d7377',
      stroke: ink,
      strokeWidth: 2,
      label: 'Display',
    },
  }),
  item({
    elementId: 'flow-predefined',
    name: 'Predefined process',
    category: 'flowchart',
    tags: ['flowchart', 'subprocess'],
    defaultWidth: 200,
    defaultHeight: 100,
    factory: {
      type: 'diagram',
      diagram: 'predefined',
      fill: '#45566e',
      stroke: ink,
      strokeWidth: 2,
      label: 'Subprocess',
    },
  }),
  item({
    elementId: 'flow-connector',
    name: 'Connector',
    category: 'flowchart',
    tags: ['flowchart', 'connector'],
    defaultWidth: 72,
    defaultHeight: 72,
    factory: {
      type: 'diagram',
      diagram: 'connector',
      fill: gold,
      stroke: ink,
      strokeWidth: 2,
      label: 'A',
    },
  }),
  item({
    elementId: 'flow-stored',
    name: 'Stored data',
    category: 'flowchart',
    tags: ['flowchart', 'database', 'storage'],
    defaultWidth: 180,
    defaultHeight: 110,
    factory: {
      type: 'diagram',
      diagram: 'stored-data',
      fill: sky,
      stroke: ink,
      strokeWidth: 2,
      label: 'Stored data',
    },
  }),
  item({
    elementId: 'flow-merge',
    name: 'Merge',
    category: 'flowchart',
    tags: ['flowchart', 'merge'],
    defaultWidth: 160,
    defaultHeight: 100,
    factory: {
      type: 'diagram',
      diagram: 'merge',
      fill: teal,
      stroke: ink,
      strokeWidth: 2,
      label: 'Merge',
    },
  }),
  item({
    elementId: 'flow-offpage',
    name: 'Off-page connector',
    category: 'flowchart',
    tags: ['flowchart', 'off-page'],
    defaultWidth: 120,
    defaultHeight: 140,
    factory: {
      type: 'diagram',
      diagram: 'off-page',
      fill: '#5b6abf',
      stroke: ink,
      strokeWidth: 2,
      label: 'Off-page',
    },
  }),

  // —— UML ——
  item({
    elementId: 'uml-actor',
    name: 'Actor',
    category: 'uml',
    tags: ['uml', 'actor', 'usecase'],
    defaultWidth: 90,
    defaultHeight: 160,
    factory: {
      type: 'diagram',
      diagram: 'actor',
      fill: 'transparent',
      stroke: ink,
      strokeWidth: 2,
    },
  }),
  item({
    elementId: 'uml-usecase',
    name: 'Use case',
    category: 'uml',
    tags: ['uml', 'usecase', 'ellipse'],
    defaultWidth: 200,
    defaultHeight: 100,
    factory: {
      type: 'diagram',
      diagram: 'usecase',
      fill: slate,
      stroke: ink,
      strokeWidth: 2,
      label: 'Use case',
    },
  }),
  item({
    elementId: 'uml-class',
    name: 'Class',
    category: 'uml',
    tags: ['uml', 'class', 'oop'],
    defaultWidth: 200,
    defaultHeight: 180,
    factory: {
      type: 'diagram',
      diagram: 'class',
      fill: '#f7f9fc',
      stroke: ink,
      strokeWidth: 2,
    },
  }),
  item({
    elementId: 'uml-package',
    name: 'Package',
    category: 'uml',
    tags: ['uml', 'package'],
    defaultWidth: 200,
    defaultHeight: 140,
    factory: {
      type: 'diagram',
      diagram: 'package',
      fill: gold,
      stroke: ink,
      strokeWidth: 2,
    },
  }),
  item({
    elementId: 'uml-note',
    name: 'Note',
    category: 'uml',
    tags: ['uml', 'note', 'comment'],
    defaultWidth: 160,
    defaultHeight: 120,
    factory: {
      type: 'diagram',
      diagram: 'note',
      fill: '#fff3bf',
      stroke: ink,
      strokeWidth: 2,
      label: 'Note',
    },
  }),
  item({
    elementId: 'uml-component',
    name: 'Component',
    category: 'uml',
    tags: ['uml', 'component'],
    defaultWidth: 200,
    defaultHeight: 110,
    factory: {
      type: 'diagram',
      diagram: 'component',
      fill: teal,
      stroke: ink,
      strokeWidth: 2,
    },
  }),

  // —— Arrows ——
  item({
    elementId: 'arrow-right',
    name: 'Arrow right',
    category: 'arrows',
    tags: ['arrow', 'right', 'corel'],
    defaultWidth: 200,
    defaultHeight: 80,
    factory: {
      type: 'diagram',
      diagram: 'arrow-right',
      fill: teal,
      stroke: ink,
      strokeWidth: 1,
    },
  }),
  item({
    elementId: 'arrow-left',
    name: 'Arrow left',
    category: 'arrows',
    tags: ['arrow', 'left'],
    defaultWidth: 200,
    defaultHeight: 80,
    factory: {
      type: 'diagram',
      diagram: 'arrow-left',
      fill: sky,
      stroke: ink,
      strokeWidth: 1,
    },
  }),
  item({
    elementId: 'arrow-both',
    name: 'Double arrow',
    category: 'arrows',
    tags: ['arrow', 'both', 'bidirectional'],
    defaultWidth: 220,
    defaultHeight: 70,
    factory: {
      type: 'diagram',
      diagram: 'arrow-both',
      fill: '#5b6abf',
      stroke: ink,
      strokeWidth: 1,
    },
  }),
  item({
    elementId: 'arrow-chevron',
    name: 'Chevron',
    category: 'arrows',
    tags: ['arrow', 'chevron', 'block'],
    defaultWidth: 180,
    defaultHeight: 90,
    factory: {
      type: 'diagram',
      diagram: 'chevron',
      fill: gold,
      stroke: ink,
      strokeWidth: 1,
    },
  }),
  item({
    elementId: 'arrow-bent',
    name: 'Elbow arrow',
    category: 'arrows',
    tags: ['arrow', 'elbow', 'connector'],
    defaultWidth: 160,
    defaultHeight: 120,
    factory: {
      type: 'diagram',
      diagram: 'arrow-bent',
      fill: ink,
      stroke: ink,
      strokeWidth: 3,
    },
  }),
  item({
    elementId: 'arrow-callout',
    name: 'Callout arrow',
    category: 'arrows',
    tags: ['arrow', 'callout'],
    defaultWidth: 200,
    defaultHeight: 90,
    factory: {
      type: 'diagram',
      diagram: 'arrow-callout',
      fill: '#b54708',
      stroke: ink,
      strokeWidth: 1,
      label: 'Callout',
    },
  }),
];
